import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useForm } from "react-hook-form";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { FiUpload } from "react-icons/fi";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { useBadInventoryActionsAPI } from "@/app/hooks/api_hooks/Inventory/BadInventory/useBadInventoryActionsAPI";
import { useNavigate } from "react-router";

export interface IShrinkImageModalProps {
  images: string[];
}

const ShrinkImageModalContent = ({ selectedImage, setSelectedImage, showImageError, formHook, setImages }) => {
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();

  const handleRemoveImage = (index: number) => {
    setSelectedImage((prevImages) => prevImages.filter((_, i) => i !== index));
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const allowedTypes = ["image/jpeg", "image/png"];
      const images = Array.from(event.target.files);
      const invalidFiles = images.filter((file) => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        toast.error("Only JPG, PNG files are allowed.");
        event.target.value = "";
        return;
      }
      const files = Array.from(event.target.files);
      const uploadedImages = await Promise.all(
        files.map(async (file) => {
          const result = await uploadToS3(
            file,
            formHook.getValues("shrink_image_id") ? formHook.getValues("shrink_image_id") : "shrink",
            "shrink_image",
          );
          if (result.status) {
            setSelectedImage((prev) => [
              ...prev,
              {
                file_url: result.message,
              },
            ]);
            return result.message;
          }
          toast.error(`Failed to upload ${file.name}`);
          return null;
        }),
      );
      const validImages = uploadedImages.filter((img) => img !== null);

      const currentImages = formHook.getValues("images") || [];
      formHook.setValue("images", [...currentImages, ...new Set(validImages?.flat())]);
      setImages([...currentImages, ...new Set(validImages?.flat())]);
    }
  };

  return (
    <div>
      <div className="justify-start items-center gap-16 flex mt-5 mb-5">
        <div className="flex justify-start items-center gap-1 p-2.5 w-[85px] h-[38px]">
          <span className="font-normal text-black text-xs">Images</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {selectedImage.map((image, index) => (
            <div key={index} className="relative mt-3 mr-3">
              <img src={image.file_url} alt="Selected" className="w-20 h-14 object-contain" />
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md"
              >
                &times;
              </button>
            </div>
          ))}
          {selectedImage.length > 0 ? (
            <label className="flex items-center text-center gap-2 text-sm font-medium text-[rgb(54,130,238)] cursor-pointer">
              <FiUpload /> Upload images
              <input
                type="file"
                accept="image/png, image/jpeg"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
            </label>
          ) : null}
        </div>
        <div>
          {selectedImage.length === 0 && (
            <div
              className={`border ${selectedImage.length === 0 && showImageError ? "border-red-600" : "border-gray-200"} p-5 rounded-md w-[290px] flex flex-col items-center`}
            >
              <label className="flex items-center text-center gap-2 font-medium text-[rgb(54,130,238)] cursor-pointer">
                <FiUpload />
                Upload
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
              <p className="mt-2 text-gray-600 text-center">Each image must not exceed 5MB</p>
            </div>
          )}
          {showImageError && selectedImage.length === 0 && (
            <p className="mt-2 text-xs text-red-600">Image is required</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ShrinkImageModal = () => {
  const { setInvisible, setModalData } = useModalActions("SHRINK_IMAGE_MODAL");
  const formHook = useForm<IShrinkImageModalProps>({
    mode: "onChange",
  });
  const modalState = useModalState("SHRINK_IMAGE_MODAL");
  const [showImageError, setShowImageError] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ base64String: string; fileExtension: string }[]>([]);
  const { execute: actionsOnBad } = useBadInventoryActionsAPI(modalState?.refreshData);
  const [images, setImages] = useState([]);

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // ✅ read query params
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const processId = queryParams.get("closing_process");
  const isClinicView = queryParams.get("is_clinic");
  const city_id = queryParams.get("city_id");

  const resetModalState = () => {
    setSelectedImage([]);
    setShowImageError(false);
    formHook.reset({ images: [] });
  };

  useEffect(() => {
    formHook.clearErrors();
    if (status) {
      setSelectedImage([]);
      resetModalState();
      setModalData({ status: true });
      formHook.reset({ images: [] });
    }
  }, [status]);

  useEffect(() => {
    if (modalState?.visible) {
      resetModalState();
      formHook.reset({ images: [] });
    }
  }, [modalState?.visible]);

  const handleSubmit = async () => {
    if (selectedImage.length === 0) {
      setShowImageError(true);
      return;
    }
    await actionsOnBad({
      bad_inventory_id: modalState.bad_inventory_id,
      action: "shrink",
      reason_of_rejection: images.join(","),
    });
    processId &&
      navigate(
        `/app/closing/day-closing?closing_process=${processId}&city_id=${city_id}&clinic_ids=${queryClinics}&is_clinic=${isClinicView}&shift=${queryShift}&t=${Date.now()}`,
      );
    setInvisible();
  };

  return (
    <ModalPrompt
      modalId="SHRINK_IMAGE_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
      button={{
        primaryButtonText: "Update",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: `px-6 py-[9px] rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex cursor-pointer bg-sky-900 hover`,
        primaryCallback: () => {
          resetModalState();
          formHook.reset();
          handleSubmit();
        },
        secondaryCallback: () => {
          resetModalState();
          formHook.reset();
          setInvisible();
        },
        onCrossClick: () => {
          resetModalState();
          formHook.reset();
          setInvisible();
        },
      }}
      title="Shrink"
      content={
        <ShrinkImageModalContent
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          showImageError={showImageError}
          formHook={formHook}
          setImages={setImages}
        />
      }
    />
  );
};

export default ShrinkImageModal;
